import React, { useState, useEffect, useContext, useRef, useCallback } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Box from "@material-ui/core/Box";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import { i18n } from "../../translate/i18n";
import { HelpOutline, Send, AttachFile } from "@material-ui/icons";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { translateBackendError } from "../../utils/toastPolicy";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import AIHubLogsModal from "../AIHubLogsModal";

const useStyles = makeStyles(theme => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    multFieldLine: {
        display: "flex",
        "& > *:not(:last-child)": {
            marginRight: theme.spacing(1),
        },
    },

    btnWrapper: {
        position: "relative",
    },

    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
    },
    inlineRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: theme.spacing(2),
    },
    contentRow: {
        display: "flex",
        gap: theme.spacing(2),
        alignItems: "stretch",
    },
    formColumn: {
        flex: 1,
        minWidth: 0,
    },
    // ==== Painel de teste ====
    testPanel: {
        width: 340,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        overflow: "hidden",
        height: 480,
    },
    testHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: theme.spacing(1, 1.5),
        background: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
    },
    testHeaderTitle: {
        fontWeight: 600,
        fontSize: 14,
        lineHeight: 1.2,
    },
    testHeaderSubtitle: {
        fontSize: 11,
        opacity: 0.85,
    },
    clearButton: {
        color: theme.palette.primary.contrastText,
        borderColor: "rgba(255,255,255,0.6)",
        fontSize: 11,
        padding: "2px 10px",
    },
    testMessages: {
        flex: 1,
        overflowY: "auto",
        padding: theme.spacing(1.5),
        background: theme.palette.type === "dark" ? theme.palette.background.default : "#f5f6fa",
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
    },
    emptyState: {
        margin: "auto",
        textAlign: "center",
        color: theme.palette.text.secondary,
        padding: theme.spacing(2),
    },
    bubble: {
        maxWidth: "85%",
        padding: theme.spacing(1, 1.5),
        borderRadius: 12,
        fontSize: 13,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
    },
    bubbleUser: {
        alignSelf: "flex-end",
        background: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        borderBottomRightRadius: 2,
    },
    bubbleAi: {
        alignSelf: "flex-start",
        background: theme.palette.type === "dark" ? theme.palette.background.paper : "#fff",
        border: `1px solid ${theme.palette.divider}`,
        borderBottomLeftRadius: 2,
    },
    bubbleInfo: {
        alignSelf: "flex-start",
        fontStyle: "italic",
        fontSize: 12,
        color: theme.palette.text.secondary,
        background: "transparent",
        padding: theme.spacing(0.25, 1),
    },
    bubbleError: {
        alignSelf: "flex-start",
        background: theme.palette.type === "dark" ? "#4a1f1f" : "#fdecea",
        color: theme.palette.type === "dark" ? "#f5c6c6" : "#611a15",
        border: `1px solid ${theme.palette.type === "dark" ? "#8b3a3a" : "#f5c6cb"}`,
    },
    typingIndicator: {
        alignSelf: "flex-start",
        fontStyle: "italic",
        fontSize: 12,
        color: theme.palette.text.secondary,
        padding: theme.spacing(0.25, 1),
    },
    testInputRow: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        padding: theme.spacing(1),
        borderTop: `1px solid ${theme.palette.divider}`,
    },
}));

const isImageUrl = (url) => /\.(jpe?g|png|gif|webp|bmp)(\?.*)?$/i.test(url || "");
const isAudioUrl = (url) => /\.(ogg|oga|opus|mp3|m4a|aac|wav|amr)(\?.*)?$/i.test(url || "");

const PromptSchema = Yup.object().shape({
    name: Yup.string()
        .max(100, () => i18n.t("promptModal.validation.nameTooLong"))
        .required(() => i18n.t("promptModal.validation.nameRequired")),
    aiHubAgentId: Yup.string()
        .required(() => i18n.t("promptModal.validation.agentIdRequired")),
    aiHubBaseUrl: Yup.string()
        .trim()
        .matches(/^https?:\/\/.+/, {
            message: () => i18n.t("promptModal.validation.invalidBaseUrl"),
            excludeEmptyString: true
        })
});

const PromptModal = ({ open, onClose, promptId }) => {
    const classes = useStyles();
    const { user } = useContext(AuthContext);
    const companyId = user?.companyId;
    const socketManager = useContext(SocketContext);

    const initialState = {
        name: "",
        aiHubAgentId: "",
        aiHubBaseUrl: "",
        aiHubEnabled: true,
        integrateHumanMessages: false,
        signMessage: false
    };

    const [prompt, setPrompt] = useState(initialState);

    // ==== Estado do chat de teste ====
    const [sessionId, setSessionId] = useState(() => uuidv4());
    const [testMessages, setTestMessages] = useState([]);
    const [testInput, setTestInput] = useState("");
    const [aiTyping, setAiTyping] = useState(false);
    const [logsOpen, setLogsOpen] = useState(false);
    const fileInputRef = useRef(null);
    const messagesEndRef = useRef(null);
    const sessionIdRef = useRef(sessionId);
    sessionIdRef.current = sessionId;
    // O envio de teste vive fora do Formik, então o valor do interruptor é espelhado
    // aqui para o teste usar o que está na tela AGORA (mesmo antes de salvar).
    const signMessageRef = useRef(false);

    useEffect(() => {
        const fetchPrompt = async () => {
            if (!promptId) {
                setPrompt(initialState);
                signMessageRef.current = false;
                return;
            }
            try {
                const { data } = await api.get(`/prompt/${promptId}`);
                signMessageRef.current = data.signMessage || false;
                setPrompt(prevState => {
                    return {
                        ...prevState,
                        ...data,
                        aiHubBaseUrl: data.aiHubBaseUrl || "",
                        integrateHumanMessages: data.integrateHumanMessages || false,
                        signMessage: data.signMessage || false
                    };
                });
            } catch (err) {
                toastError(err);
            }
        };

        fetchPrompt();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [promptId, open]);

    // Restaura a sessão de teste ao abrir o modal (persistida por agente no localStorage)
    useEffect(() => {
        if (!open) return;
        setTestInput("");
        setAiTyping(false);

        if (!promptId) {
            setSessionId(uuidv4());
            setTestMessages([]);
            return;
        }

        const storageKey = `aiHubTestSession-${promptId}`;
        let sid = localStorage.getItem(storageKey);
        if (!sid) {
            sid = uuidv4();
            localStorage.setItem(storageKey, sid);
        }
        setSessionId(sid);
        sessionIdRef.current = sid;

        // Carrega o histórico persistido no backend
        (async () => {
            try {
                const { data } = await api.get(`/prompt/${promptId}/test-messages`, {
                    params: { sessionId: sid }
                });
                const restored = (data.messages || []).map(m => ({
                    id: `db-${m.id}`,
                    from: m.role === "user" ? "user"
                        : m.role === "error" ? "error"
                        : m.role === "info" ? "info"
                        : "ai",
                    text: m.content,
                    url: m.mediaUrl || undefined
                }));
                setTestMessages(restored);
            } catch (err) {
                setTestMessages([]);
            }
        })();
    }, [open, promptId]);

    // Assina o socket para receber os callbacks do hub na sessão de teste
    useEffect(() => {
        if (!open || !promptId || !companyId) return;

        const socket = socketManager.getSocket(companyId);
        const eventName = `company-${companyId}-aiHubTest`;

        // O estado de "digitando" é 100% dirigido pelo backend (typing_on/typing_off),
        // que espelha a máquina de estados real: liga no ACK do hub, renova no callback
        // `typing`, expira no teto de duração e para em final/attachment/error.
        const handler = (data) => {
            if (!data || data.sessionId !== sessionIdRef.current) return;

            if (data.type === "typing_on") {
                setAiTyping(true);
                return;
            }
            if (data.type === "typing_off") {
                setAiTyping(false);
                return;
            }
            if (data.type === "intermediate") {
                if (data.message) {
                    setTestMessages(prev => [...prev, { id: uuidv4(), from: "info", text: data.message }]);
                }
                return;
            }
            if (data.type === "final") {
                setTestMessages(prev => [...prev, { id: uuidv4(), from: "ai", text: data.content || "" }]);
                return;
            }
            if (data.type === "attachment") {
                setTestMessages(prev => [...prev, {
                    id: uuidv4(),
                    from: "ai",
                    text: data.caption || `[${data.attachment_type || i18n.t("forwardMessageModal.previewFile")}]`,
                    url: data.url
                }]);
                return;
            }
            if (data.type === "error") {
                // Mesmo texto que o cliente receberia no WhatsApp (detalhes ficam nos Logs)
                setTestMessages(prev => [...prev, {
                    id: uuidv4(),
                    from: "error",
                    text: data.content || i18n.t("promptModal.test.processingError")
                }]);
            }
        };

        socket.on(eventName, handler);
        return () => {
            socket.off(eventName, handler);
        };
    }, [open, promptId, companyId, socketManager]);

    // Auto-scroll para a última mensagem
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [testMessages, aiTyping]);

    // Envio assíncrono: não bloqueia o input — o usuário pode mandar várias
    // mensagens em sequência enquanto a IA ainda processa (como no caso real)
    const handleSendTestMessage = useCallback(async () => {
        const text = testInput.trim();
        if (!text || !promptId) return;

        setTestInput("");
        const bubbleId = uuidv4();
        setTestMessages(prev => [...prev, { id: bubbleId, from: "user", text }]);
        // O "digitando" NÃO liga aqui: quem liga é o backend após o ACK do hub
        // (evento typing_on), espelhando o fluxo real.

        try {
            await api.post(`/prompt/${promptId}/test-message`, {
                sessionId: sessionIdRef.current,
                message: text,
                signMessage: signMessageRef.current
            });
        } catch (err) {
            const detail = translateBackendError(err.response?.data?.error, err.message);
            setTestMessages(prev => [...prev, { id: uuidv4(), from: "error", text: detail }]);
        }
    }, [testInput, promptId]);

    // Envio de anexo (imagem/áudio); o texto digitado vai como legenda
    const handleSendTestFile = useCallback(async (file) => {
        if (!file || !promptId) return;

        const caption = testInput.trim();
        setTestInput("");
        const bubbleId = uuidv4();
        setTestMessages(prev => [...prev, {
            id: bubbleId,
            from: "user",
            text: `📎 ${file.name}${caption ? ` — ${caption}` : ""}`
        }]);

        try {
            const formData = new FormData();
            formData.append("typeArch", "aiHubTest");
            formData.append("sessionId", sessionIdRef.current);
            formData.append("message", caption);
            formData.append("signMessage", signMessageRef.current ? "true" : "false");
            formData.append("media", file);

            const { data } = await api.post(`/prompt/${promptId}/test-message`, formData);
            // Atualiza a bolha com o conteúdo/URL definitivos (preview da mídia)
            setTestMessages(prev => prev.map(m =>
                m.id === bubbleId ? { ...m, text: data.content || m.text, url: data.mediaUrl || undefined } : m
            ));
        } catch (err) {
            const detail = translateBackendError(err.response?.data?.error, err.message);
            setTestMessages(prev => [...prev, { id: uuidv4(), from: "error", text: detail }]);
        }
    }, [testInput, promptId]);

    const handleClearTestChat = async () => {
        try {
            await api.delete(`/prompt/${promptId}/test-session/${sessionIdRef.current}`);
        } catch (err) {
            // sessão pode não existir mais; ignorar
        }
        const newSid = uuidv4();
        localStorage.setItem(`aiHubTestSession-${promptId}`, newSid);
        setSessionId(newSid);
        sessionIdRef.current = newSid;
        setTestMessages([]);
        setAiTyping(false);
    };

    const handleClose = () => {
        setPrompt(initialState);
        onClose();
    };

    const handleSavePrompt = async values => {
        if (!values.name || values.name.trim() === "") {
            toast.error(i18n.t("promptModal.validation.nameRequired"));
            return;
        }
        if (!values.aiHubAgentId || values.aiHubAgentId.trim() === "") {
            toast.error(i18n.t("promptModal.validation.agentIdRequired"));
            return;
        }

        const promptData = {
            ...values,
            aiHubEnabled: values.aiHubEnabled !== false,
            aiHubBaseUrl: (values.aiHubBaseUrl || "").trim(),
            integrateHumanMessages: Boolean(values.integrateHumanMessages),
            signMessage: Boolean(values.signMessage)
        };

        try {
            if (promptId) {
                await api.put(`/prompt/${promptId}`, promptData);
            } else {
                await api.post("/prompt", promptData);
            }
            toast.success(i18n.t("promptModal.success"));
            handleClose();
        } catch (err) {
            toastError(err);
        }
    };

    return (
        <div className={classes.root}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth={promptId ? "lg" : "md"}
                scroll="paper"
                fullWidth
            >
                <DialogTitle id="form-dialog-title">
                    {promptId
                        ? `${i18n.t("promptModal.title.edit")}`
                        : `${i18n.t("promptModal.title.add")}`}
                </DialogTitle>
                <Formik
                    initialValues={prompt}
                    enableReinitialize={true}
                    validationSchema={PromptSchema}
                    onSubmit={(values, actions) => {
                        setTimeout(() => {
                            handleSavePrompt(values);
                            actions.setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ touched, errors, isSubmitting, values, setFieldValue }) => (
                        <Form style={{ width: "100%" }}>
                            <DialogContent dividers>
                                <Box className={classes.contentRow}>
                                    <Box className={classes.formColumn}>
                                        <Field
                                            as={TextField}
                                            label={i18n.t("promptModal.form.name")}
                                            name="name"
                                            error={touched.name && Boolean(errors.name)}
                                            helperText={touched.name && errors.name}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            required
                                        />
                                        <Field
                                            as={TextField}
                                            label={i18n.t("promptModal.form.aiHubAgentId")}
                                            name="aiHubAgentId"
                                            error={touched.aiHubAgentId && Boolean(errors.aiHubAgentId)}
                                            helperText={touched.aiHubAgentId && errors.aiHubAgentId || i18n.t("promptModal.form.aiHubAgentIdHelper")}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            required
                                        />
                                        <Field
                                            as={TextField}
                                            label={i18n.t("promptModal.form.aiHubBaseUrlLabel")}
                                            name="aiHubBaseUrl"
                                            placeholder="https://seu-hub-de-ia.com.br"
                                            error={touched.aiHubBaseUrl && Boolean(errors.aiHubBaseUrl)}
                                            helperText={touched.aiHubBaseUrl && errors.aiHubBaseUrl || i18n.t("promptModal.form.aiHubBaseUrlHelper")}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        />
                                        <Box className={classes.inlineRow}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={values.integrateHumanMessages || false}
                                                        onChange={(e) => setFieldValue("integrateHumanMessages", e.target.checked)}
                                                        color="primary"
                                                    />
                                                }
                                                label={i18n.t("promptModal.form.integrateHumanMessages")}
                                            />
                                            <Tooltip title={i18n.t("promptModal.form.integrateHumanMessagesTooltip")}>
                                                <HelpOutline fontSize="small" color="action" />
                                            </Tooltip>
                                        </Box>
                                        <Box className={classes.inlineRow}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={values.signMessage || false}
                                                        onChange={(e) => {
                                                            setFieldValue("signMessage", e.target.checked);
                                                            signMessageRef.current = e.target.checked;
                                                        }}
                                                        color="primary"
                                                    />
                                                }
                                                label={i18n.t("promptModal.form.signMessage")}
                                            />
                                            <Tooltip title={i18n.t("promptModal.form.signMessageTooltip")}>
                                                <HelpOutline fontSize="small" color="action" />
                                            </Tooltip>
                                        </Box>
                                    </Box>

                                    {promptId && (
                                        <Box className={classes.testPanel}>
                                            <Box className={classes.testHeader}>
                                                <Box>
                                                    <Typography className={classes.testHeaderTitle}>
                                                        💬 {i18n.t("promptModal.test.title")}
                                                    </Typography>
                                                    <Typography className={classes.testHeaderSubtitle}>
                                                        {i18n.t("promptModal.test.subtitle")}
                                                    </Typography>
                                                </Box>
                                                <Box style={{ display: "flex", gap: 6 }}>
                                                    <Button
                                                        type="button"
                                                        size="small"
                                                        variant="outlined"
                                                        className={classes.clearButton}
                                                        onClick={() => setLogsOpen(true)}
                                                    >
                                                        Logs
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        size="small"
                                                        variant="outlined"
                                                        className={classes.clearButton}
                                                        onClick={handleClearTestChat}
                                                    >
                                                        {i18n.t("common.clear")}
                                                    </Button>
                                                </Box>
                                            </Box>
                                            <Box className={classes.testMessages}>
                                                {testMessages.length === 0 && !aiTyping && (
                                                    <Box className={classes.emptyState}>
                                                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                                                            {i18n.t("promptModal.test.emptyTitle")}
                                                        </Typography>
                                                        <Typography variant="caption">
                                                            {i18n.t("promptModal.test.emptyHint")}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {testMessages.map(msg => (
                                                    <Box
                                                        key={msg.id}
                                                        className={`${classes.bubble} ${
                                                            msg.from === "user" ? classes.bubbleUser
                                                            : msg.from === "error" ? classes.bubbleError
                                                            : msg.from === "info" ? classes.bubbleInfo
                                                            : classes.bubbleAi
                                                        }`}
                                                    >
                                                        {msg.text}
                                                        {msg.url && isImageUrl(msg.url) && (
                                                            <div>
                                                                <a href={msg.url} target="_blank" rel="noopener noreferrer">
                                                                    <img src={msg.url} alt="anexo" style={{ maxWidth: "100%", borderRadius: 6, marginTop: 4 }} />
                                                                </a>
                                                            </div>
                                                        )}
                                                        {msg.url && isAudioUrl(msg.url) && (
                                                            <div style={{ marginTop: 4 }}>
                                                                <audio controls src={msg.url} style={{ maxWidth: "100%", height: 32 }} />
                                                            </div>
                                                        )}
                                                        {msg.url && !isImageUrl(msg.url) && !isAudioUrl(msg.url) && (
                                                            <div>
                                                                <a href={msg.url} target="_blank" rel="noopener noreferrer">
                                                                    {i18n.t("promptModal.test.openAttachment")}
                                                                </a>
                                                            </div>
                                                        )}
                                                    </Box>
                                                ))}
                                                {aiTyping && (
                                                    <Typography className={classes.typingIndicator}>
                                                        digitando...
                                                    </Typography>
                                                )}
                                                <div ref={messagesEndRef} />
                                            </Box>
                                            <Box className={classes.testInputRow}>
                                                <input
                                                    type="file"
                                                    accept="image/*,audio/*"
                                                    ref={fileInputRef}
                                                    style={{ display: "none" }}
                                                    onChange={(e) => {
                                                        const file = e.target.files?.[0];
                                                        e.target.value = "";
                                                        if (file) handleSendTestFile(file);
                                                    }}
                                                />
                                                <IconButton
                                                    type="button"
                                                    size="small"
                                                    onClick={() => fileInputRef.current?.click()}
                                                    title={i18n.t("promptModal.test.attachTooltip")}
                                                >
                                                    <AttachFile />
                                                </IconButton>
                                                <TextField
                                                    placeholder={i18n.t("promptModal.test.inputPlaceholder")}
                                                    value={testInput}
                                                    onChange={(e) => setTestInput(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter" && !e.shiftKey) {
                                                            e.preventDefault();
                                                            handleSendTestMessage();
                                                        }
                                                    }}
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                />
                                                <IconButton
                                                    type="button"
                                                    color="primary"
                                                    size="small"
                                                    onClick={handleSendTestMessage}
                                                    disabled={!testInput.trim()}
                                                >
                                                    <Send />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={handleClose}
                                    color="secondary"
                                    disabled={isSubmitting}
                                    variant="outlined"
                                >
                                    {i18n.t("promptModal.buttons.cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    disabled={isSubmitting}
                                    variant="contained"
                                    className={classes.btnWrapper}
                                >
                                    {promptId
                                        ? `${i18n.t("promptModal.buttons.okEdit")}`
                                        : `${i18n.t("promptModal.buttons.okAdd")}`}
                                    {isSubmitting && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
            {promptId && (
                <AIHubLogsModal
                    open={logsOpen}
                    onClose={() => setLogsOpen(false)}
                    promptId={promptId}
                />
            )}
        </div>
    );
};

export default PromptModal;
